import { LODLevel } from '../Types';

/**
 * Default LOD configuration with FOV thresholds and priorities
 */
export const DEFAULT_LOD_LEVELS: LODLevel[] = [
  { level: 1, tilesPerFace: 1, fovThreshold: { min: 60, max: 120 }, priority: 1 },
  { level: 2, tilesPerFace: 2, fovThreshold: { min: 30, max: 60 }, priority: 2 },
  { level: 3, tilesPerFace: 4, fovThreshold: { min: 15, max: 30 }, priority: 3 },
  { level: 4, tilesPerFace: 8, fovThreshold: { min: 0, max: 15 }, priority: 4 },
];

/**
 * Timing and throttling constants
 */
export const TIMING = {
  /** Viewport update throttle in milliseconds */
  VIEWPORT_UPDATE_THROTTLE: 100,
  
  /** Minimum time before tiles can be removed after viewport changes */
  REMOVAL_STABILITY_MS: 800,
  
  /** Minimum time to wait during LOD transitions (normal) */
  MIN_TRANSITION_TIME: 1000,
  
  /** Minimum time to wait during rapid LOD transitions */
  MIN_RAPID_TRANSITION_TIME: 1500,
  
  /** Maximum time to wait for transition completion before forcing */
  MAX_TRANSITION_TIME: 4000,
  
  /** Interval for checking transition completion */
  TRANSITION_CHECK_INTERVAL: 200,
  
  /** Initial delay before starting transition completion checks */
  TRANSITION_INITIAL_DELAY: 400,
  
  /** Time to wait for texture loading fallback */
  TEXTURE_LOAD_TIMEOUT: 200,
  
  /** Delay between removal batches */
  REMOVAL_BATCH_DELAY: 100,
} as const;

/**
 * Safety and coverage thresholds
 */
export const COVERAGE = {
  /** Minimum tiles required before any removal operations */
  MIN_SAFE_TILE_COUNT: 24,
  
  /** Minimum tiles required for basic operations */
  MIN_REQUIRED_TILES: 12,
  
  /** Minimum tiles per cube face for safe removal */
  MIN_TILES_PER_FACE: 4,
  
  /** Minimum number of faces that must have tile coverage */
  MIN_FACE_COVERAGE: 5,
  
  /** Minimum face coverage during rapid transitions */
  MIN_RAPID_TRANSITION_FACE_COVERAGE: 5,
  
  /** Minimum tiles required for excellent coverage */
  EXCELLENT_COVERAGE_MULTIPLIER: 4,
  
  /** Maximum percentage of tiles to remove at once */
  MAX_REMOVAL_RATIO: 0.25,
  
  /** Maximum percentage of tiles to remove during transitions */
  MAX_TRANSITION_REMOVAL_RATIO: 0.3,
} as const;

/**
 * Loading and concurrency limits
 */
export const LOADING = {
  /** Maximum concurrent tile loads */
  MAX_CONCURRENT_LOADS: 6,
  
  /** Default maximum cache size */
  DEFAULT_MAX_CACHE_SIZE: 100,
  
  /** High priority for base tiles */
  BASE_TILE_PRIORITY: 10,
  
  /** Very high priority for FOV tiles at highest LOD */
  HIGHEST_LOD_FOV_PRIORITY: 5000,
  
  /** Low priority for out-of-FOV tiles */
  OUT_OF_FOV_PRIORITY: 1,
  
  /** Maximum frames to wait for mesh rendering */
  MAX_RENDER_WAIT_FRAMES: 5,
} as const;

/**
 * Render order and visual constants
 */
export const RENDERING = {
  /** Base render order for tiles */
  BASE_RENDER_ORDER: 100,
  
  /** Polygon offset factor for preventing z-fighting */
  POLYGON_OFFSET_FACTOR: -1,
  
  /** Polygon offset units for preventing z-fighting */
  POLYGON_OFFSET_UNITS: -1,
  
  /** Default tile padding */
  DEFAULT_TILE_PADDING: 1,
  
  /** Default transition buffer */
  DEFAULT_TRANSITION_BUFFER: 5,
} as const;

/**
 * Cube face mapping for URL generation
 */
export const FACE_MAPPING = {
  f: 'front',
  b: 'back',
  l: 'left',
  r: 'right',
  u: 'up',
  d: 'down',
} as const;

/**
 * All cube faces array
 */
export const ALL_CUBE_FACES = ['f', 'b', 'l', 'r', 'u', 'd'] as const;
